import { MeasurementUnitEnum } from 'domains/measurementDomain';
import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  Affix,
  AutoComplete,
  Checkbox,
  Divider,
  Form,
  Input,
  InputNumber,
  PageHeader,
  Popconfirm,
  Radio,
  Select,
  Skeleton,
  Switch,
} from 'antd';
import { ColumnsType } from 'antd/lib/table/interface';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxInputImage } from 'components';
import { ArrowLeftIcon } from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import { RevxSpin } from 'components/RevxSpin';
import { RevxTable } from 'components/RevxTable';
import { Headline } from 'components/RevxTypography';
import {
  BalanceSelectPaymentMethodsEnum,
  Contact,
  isCustomerOrg,
  isSaasOrg,
  localeForCountry,
  ORG_LOCALES,
  PartialOrg,
  WorkspaceTableConfig,
} from 'domains/orgDomain';
import { useUser } from 'hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import states from 'states-us';
import { isUnitedStates } from 'utils/address';
import timezones from 'timezones-list';
import { ADMIN_DETAIL_AFFIX_TOP, ALBERTSONS_ORGS } from 'utils/constants';
import { pipedStrToOptions } from 'utils/misc';
import { isOrgAdmin, isSysAdmin } from 'utils/rbac';
import {
  RULE_EMAIL_TRIM,
  RULE_NUMBER,
  RULE_TEXT_LEN_255,
  RULE_YEAR,
} from 'utils/validators';
import AddressValidation, {
  ValidationRef,
} from '../ShippingAdmin/components/AddressValidation';
import { useMemoizedFn, useMount } from 'ahooks';
import contactService from 'services/contactService';
import { addressTranslator } from 'utils/address';

import paymentService, { StripeAccountInfo } from 'services/paymentService';
import { OrgTypeEnum } from '../OrgAdmin/OrgTypeSelectModal';
import OrgTypeSelectModal from '../OrgAdmin/OrgTypeSelectModal';
import { PlatformRoleEnum } from 'domains/roleDomain';
import { toast } from 'components/RevxMessage';
import { CostDistributionImportModal } from './CostDistributionImportModal';

const { creditCard, invoice, achDebit, achCredit, internal } =
  BalanceSelectPaymentMethodsEnum;

const statesAbbrevs = states.map((x) => x.abbreviation);

type WorkspaceTableConfigKey = keyof WorkspaceTableConfig;
type OrgItemLabelTableCol = {
  name: string;
  standardLabel: string;
  preConfigLabel: string;
  isSourcer: boolean;
  type: WorkspaceTableConfigKey;
  role: 'supplier' | 'sourcer';
};

enum WorkspaceTableConfigKeyEnum {
  inProductionLabel = 'inProductionLabel',
  shippingLabel = 'shippingLabel',
  billingLabel = 'billingLabel',
  invoicingLabel = 'invoicingLabel',
}

enum TermsEnum {
  Net15 = 'Net15',
  Net30 = 'Net30',
  Net45 = 'Net45',
  Net60 = 'Net60',
  DueOnReceipt = 'DueOnReceipt',
}

const { inProductionLabel, invoicingLabel, shippingLabel, billingLabel } =
  WorkspaceTableConfigKeyEnum;

const { Net15, Net30, Net45, Net60, DueOnReceipt } = TermsEnum;

const invoiceSendersListMaxLength = 5;

const emailStrToArray = (str: string) => {
  return str
    .split(',')
    .map((v: string) => v.trim())
    .filter((v: string) => v);
};

const paymentMethodOptions = [
  { label: 'ACH/Auto bank debit', value: achDebit },
  { label: 'Credit card', value: creditCard },
  { label: 'Offline payments/terms', value: invoice },
  { label: 'Internal Billing', value: internal },
];

const remitAddressOptions = [{ label: 'Same As Above', value: true }];

const autoPaymentMethodOptions = [
  { label: 'ACH/Auto bank debit', value: achDebit },
  { label: 'ACH/Credit', value: achCredit },
  { label: 'Credit card', value: creditCard },
  { label: 'Offline payments/terms', value: invoice },
  { label: 'Internal Billing', value: internal },
];

const invoiceTermsOptions = [
  { label: 'Terms Net 15', value: Net15 },
  { label: 'Terms Net 30', value: Net30 },
  { label: 'Terms Net 45', value: Net45 },
  { label: 'Terms Net 60', value: Net60 },
  { label: 'Due on Receipt', value: DueOnReceipt },
];

const OrgItemForm = () => {
  const { t } = useTranslation();
  const formItemLayout = {
    labelCol: { span: 10 },
    wrapperCol: { span: 12 },
  };

  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const saving = useAppSelector((state) => state.loading.effects.org.saveOrg);
  const item = useAppSelector((state) => state.org.curOrg);
  const saasOrg = item && isSaasOrg(item);
  const customerOrg = item && isCustomerOrg(item);

  const orgList = useAppSelector((state) => state?.org?.list || []);
  const findOrgName = (orgId: number | undefined) => {
    if (orgId === undefined) return '';
    else return orgList.find((org) => org.id === orgId)?.name;
  };

  const companies = useAppSelector((state) => state.org.companies);
  const addrValidationRef = useRef<ValidationRef>(null);

  const user = useUser();
  // const orgId = useOrgId();
  const [stripeAccount, setStripeAccount] =
    useState<StripeAccountInfo | null>(null);
  const [loadingStripe, setLoadingStripe] = useState(false);
  // 判断是否显示 Stripe 按钮：SAAS 组织 + orgAdmin 角色
  const showStripeButton = useMemo(() => {
    return (
      item?.orgType === OrgTypeEnum.SAAS &&
      user?.platformRole === PlatformRoleEnum.OrganizationAdmin &&
      (!stripeAccount?.chargeEnable || !stripeAccount.detailSubmitted)
    );
  }, [
    item?.orgType,
    user?.platformRole,
    stripeAccount?.chargeEnable,
    stripeAccount?.detailSubmitted,
  ]);

  const [buyerDigitalProof, setBuyerDigitalProof] = useState<string>(() => {
    if (item) {
      if (item.buyerDigitalProofOptional !== undefined) {
        return item.buyerDigitalProofOptional ? 'optional' : 'required';
      }
      // 新建 CUSTOMER 类型组织时的默认值
      if (!item.id && item.orgType === OrgTypeEnum.CUSTOMER) {
        return 'optional';
      }
    }
    return 'required';
  });
  const [supplierDigitalProof, setSupplierDigitalProof] = useState<string>(
    () => {
      if (item) {
        if (item.supplierDigitalProofOptional !== undefined) {
          return item.supplierDigitalProofOptional ? 'optional' : 'required';
        }
        // 新建 CUSTOMER 类型组织时的默认值
        if (!item.id && item.orgType === OrgTypeEnum.CUSTOMER) {
          return 'optional';
        }
      }
      return 'required';
    }
  );
  const [sourcerDigitalProof, setSourcerDigitalProof] = useState<string>(() => {
    if (item) {
      if (item.sourcerDigitalProofOptional !== undefined) {
        return item.sourcerDigitalProofOptional ? 'optional' : 'required';
      }
      // 新建 CUSTOMER 类型组织时的默认值
      if (!item.id && item.orgType === OrgTypeEnum.CUSTOMER) {
        return 'optional';
      }
    }
    return 'required';
  });
  const [jobsAcceptDigitalProof, setJobsAcceptDigitalProof] = useState<string>(
    () => {
      if (item) {
        if (item.digitalProofRequired !== undefined) {
          return item.digitalProofRequired ? 'required' : 'optional';
        }
        // 新建 CUSTOMER 类型组织时的默认值
        if (!item.id && item.orgType === OrgTypeEnum.CUSTOMER) {
          return 'optional';
        }
      }
      return 'optional';
    }
  );
  const [groupJobsForSupplierStatus, setGroupJobsForSupplierStatus] =
    useState<boolean>(!!item?.groupJobsForSupplier);
  const [groupJobsForSourcerStatus, setGroupJobsForSourcerStatus] =
    useState<boolean>(!!item?.groupJobsForSourcer);
  // const [balancePaymentMethodData, setBalancePaymentMethodData] =
  //   useState<OrgBalancePaymentDTOItem[]>();

  const [orgContacts, setOrgContacts] = useState<Contact[]>([]);
  const [loadingOrgContacts, setLoadingOrgContacts] = useState<boolean>(false);

  const [defaultInvoiceContact, setDefaultInvoiceContact] = useState<Contact>();
  const [defaultInvoiceAddress, setDefaultInvoiceAddress] = useState<any>();

  // 添加缺少的状态变量
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState<
    string[]
  >([]);
  const [defaultPaymentMethod, setDefaultPaymentMethod] =
    useState<string | undefined>();

  // 组织类型修改相关状态
  const [orgTypeModalVisible, setOrgTypeModalVisible] =
    useState<boolean>(false);
  const [changingOrgType, setChangingOrgType] = useState<boolean>(false);

  const syncRemitFields = (allValues: any, form: any) => {
    const isRemitSameAsPhysical =
      Array.isArray(allValues.remitSamePhysicalAddress) &&
      allValues.remitSamePhysicalAddress.includes(true);

    if (isRemitSameAsPhysical) {
      form.setFieldsValue({
        remitFax: allValues.fax,
        remitAddressLine1: allValues.addressLine1,
        remitAddressLine2: allValues.addressLine2,
        remitCity: allValues.city,
        remitState: allValues.state,
        remitCountry: allValues.country,
        remitPostalCode: allValues.postalCode,
        remitZoneId: allValues.zoneId,
        remitPhone1: allValues.phone1,
        remitPhone2: allValues.phone2,
      });
    }
  };

  const defaultInvoiceAddressOptions = useMemo(() => {
    return defaultInvoiceContact?.addresses || [];
  }, [defaultInvoiceContact?.addresses]);

  // 监听 item 变化，更新数字证明相关状态
  useEffect(() => {
    if (item) {
      // 判断是否是新建CUSTOMER组织 - 这个判断要优先
      const isNewCustomerOrg =
        !item.id && item.orgType === OrgTypeEnum.CUSTOMER;

      if (isNewCustomerOrg) {
        // 新建CUSTOMER组织时，所有数字证明字段默认为optional
        setBuyerDigitalProof('optional');
        setSourcerDigitalProof('optional');
        setSupplierDigitalProof('optional');
        setJobsAcceptDigitalProof('optional');
      } else {
        // 其他情况根据item的值来设置
        setBuyerDigitalProof(
          item.buyerDigitalProofOptional ? 'optional' : 'required'
        );
        setSourcerDigitalProof(
          item.sourcerDigitalProofOptional ? 'optional' : 'required'
        );
        setSupplierDigitalProof(
          item.supplierDigitalProofOptional ? 'optional' : 'required'
        );
        setJobsAcceptDigitalProof(
          item.digitalProofRequired ? 'required' : 'optional'
        );
      }
    }
  }, [item]);

  useEffect(() => {
    if (item) {
      const { sourcerTableConfig, supplierTableConfig, invoiceSendersList } =
        item;
      const list = invoiceSendersList || [];

      form.setFieldsValue({
        sourcerTableConfig,
        supplierTableConfig,
        invoiceSendersList: list.join(','),
        balancePaymentMethods: item?.id
          ? item.balancePaymentMethods
          : [creditCard, invoice, achDebit, internal],
        remitSamePhysicalAddress: item?.remitSamePhysicalAddress ? [true] : [],
      });
      setTimeout(() => {
        const allValues = form.getFieldsValue();
        syncRemitFields(allValues, form);
      }, 0);
    }
  }, [item, form]);

  // 获取 Stripe 账户信息
  useEffect(() => {
    const fetchStripeAccount = async () => {
      console.log('showStripeButton', showStripeButton);
      console.log('item id', item?.id);
      if (showStripeButton && item?.id) {
        try {
          setLoadingStripe(true);
          const result = await paymentService.getStripeAccount();
          console.log('fetched stripe account:', result);
          setStripeAccount(result);
        } catch (error) {
          console.error('Failed to fetch Stripe account:', error);
        } finally {
          setLoadingStripe(false);
        }
      }
    };

    fetchStripeAccount();
  }, [showStripeButton, item?.id]);

  const handleStripeAccount = async () => {
    console.log('handleStripeAccount, current account:', stripeAccount);
    if (stripeAccount?.open) {
      // 已开通，打开设置链接
      const linkUrl = await paymentService.generateAccountLink();
      console.log('generated stripe account link url:', linkUrl);
      if (linkUrl) {
        window.open(stripeAccount.linkUrl, '_blank');
      }
    } else {
      // 未开通，创建 Stripe 账户
      try {
        setLoadingStripe(true);
        const result = await paymentService.createStripeAccount();
        console.log('created stripe account:', result);

        setStripeAccount(result);
        if (result.linkUrl) {
          window.open(result.linkUrl, '_blank');
        }
      } catch (error) {
        console.error('Failed to create Stripe account:', error);
      } finally {
        setLoadingStripe(false);
      }
    }
  };

  const doSave = async (values: PartialOrg) => {
    values.tags = (values.tags as unknown as string[]).join('|');
    console.log('defaultPaymentMethod', defaultPaymentMethod);
    console.log('values.balanceConfig', values.balanceConfig);
    console.log('forms.balanceConfig', form.getFieldValue('balanceConfig'));
    console.log('orgType:', item?.orgType);
    console.log('salesRep from form values:', values.salesRep);
    const remitSamePhysicalAddress = Array.isArray(
      values.remitSamePhysicalAddress
    )
      ? values.remitSamePhysicalAddress.includes(true)
      : false;
    const updated = Object.assign({}, item, values, {
      buyerDigitalProofOptional: buyerDigitalProof === 'optional',
      sourcerDigitalProofOptional: sourcerDigitalProof === 'optional',
      supplierDigitalProofOptional: supplierDigitalProof === 'optional',
      digitalProofRequired: jobsAcceptDigitalProof === 'required',
      // balanceVendorId: values.balanceConfig?.invoiceVendorId,
      // balanceBuyerId: values.balanceConfig?.buyerId,
      invoiceTerms: values.balanceConfig?.invoiceTerms,
      remitSamePhysicalAddress,
      // "Not set" has to travel as null. antd hands a cleared Select back as undefined, JSON
      // drops an undefined, and the API reads a field a request never mentioned as "leave it
      // alone" - so an org that had once been given an override could never go back to
      // following its locale.
      measurementUnit: values.measurementUnit ?? null,
      locale: values.locale ?? null,
    });
    if (defaultPaymentMethod && updated.balanceConfig) {
      console.log(
        'Default invoice contact and updated balance config exist:',
        defaultInvoiceContact,
        updated.balanceConfig
      );
      updated.balanceConfig.defaultPaymentMethod =
        defaultPaymentMethod as BalanceSelectPaymentMethodsEnum;
    }
    console.log('updated object:', updated);
    console.log('updated.salesRep:', updated.salesRep);
    await dispatch.org.saveOrg(updated);
    onClose();
  };

  const getOrgContacts = useMemoizedFn(async () => {
    try {
      if (!item?.id) return;
      const contacts = await contactService.getOrgAvlContacts(item?.id);
      setOrgContacts(contacts);

      if (item?.defaultInvoiceContactId) {
        const contact = contacts.find(
          (c) => c.id === item.defaultInvoiceContactId
        );
        setDefaultInvoiceContact(contact);

        if (contact && item?.defaultInvoiceAddressId) {
          const address = contact.addresses?.find(
            (a) => a.id === item.defaultInvoiceAddressId
          );
          setDefaultInvoiceAddress(address);
          // 同步更新表单字段
          form.setFieldsValue({
            defaultInvoiceContactId: contact.id,
            defaultInvoiceAddressId: address?.id,
          });
        } else if (contact && contact.addresses?.length > 0) {
          // 如果没有保存的地址，自动选择默认地址或第一个地址
          const defaultAddress = contact.addresses.find(
            (addr) => addr.id === contact.defaultAddressId
          );
          const selectedAddress = defaultAddress || contact.addresses[0];
          setDefaultInvoiceAddress(selectedAddress);
          form.setFieldsValue({
            defaultInvoiceContactId: contact.id,
            defaultInvoiceAddressId: selectedAddress?.id,
          });
        }
      }
    } catch {}
  });

  useMount(async () => {
    setLoadingOrgContacts(true);
    await getOrgContacts();
    setLoadingOrgContacts(false);
  });

  const onSave = async () => {
    console.log('begin save org item : ', item);
    let values;
    try {
      values = await form.validateFields();
    } catch (validationError) {
      // do nothing when form invalid
      return;
    }
    console.log('Validated form values:', values);
    values.email = values.email?.trim();
    values.gallerySupportEmail = values.gallerySupportEmail?.trim();
    values.accountingEmail = values.accountingEmail?.trim();
    values.invoiceSendersList = emailStrToArray(values.invoiceSendersList);
    const ignoreValidation = form.getFieldValue('ignoreAddressValidation');
    if (ignoreValidation) {
      doSave(values);
      return;
    }
    const ret = await addrValidationRef.current?.validate({
      addressLine: values.addressLine1,
      city: values.city,
      stateProvinceCode: values.state,
      countryCode: values.country,
      postalCode: values.postalCode,
    });

    if (ret) {
      console.log('do save values ', values);
      doSave(values);
    }
  };
  const onClose = () => {
    dispatch.org.setState({ curOrg: undefined });
  };

  // 处理组织类型修改
  const handleChangeOrgType = () => {
    setOrgTypeModalVisible(true);
  };

  const handleOrgTypeConfirm = async (orgType: OrgTypeEnum) => {
    if (!item?.id) return;

    setChangingOrgType(true);
    try {
      // 使用现有的 saveOrg 方法更新组织类型
      await dispatch.org.updateOrgType({
        id: item.id,
        orgType: orgType,
      });
      setOrgTypeModalVisible(false);
      toast(t('platform.org.orgTypeUpdatedSuccess'));
    } catch (error) {
      console.error('Failed to update org type:', error);
    } finally {
      setChangingOrgType(false);
      onClose();
    }
  };

  const handleOrgTypeCancel = () => {
    setOrgTypeModalVisible(false);
  };

  const onSaveAsNew = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (validationError) {
      // do nothing when form invalid
      return;
    }
    values.email = values.email?.trim();
    values.tags = (values.tags as unknown as string[]).join('|');
    const emailList = emailStrToArray(values.invoiceSendersList);
    values.invoiceSendersList = emailList.length > 0 ? emailList : null;
    const remitSamePhysicalAddress = Array.isArray(
      values.remitSamePhysicalAddress
    )
      ? values.remitSamePhysicalAddress.includes(true)
      : false;
    const updated = Object.assign({}, item, values, {
      remitSamePhysicalAddress,
      // See the note in doSave - a cleared Select is undefined, and undefined is not an answer.
      measurementUnit: values.measurementUnit ?? null,
      locale: values.locale ?? null,
    });
    updated.id = undefined;

    await dispatch.org.saveOrg(updated);
    onClose();
  };

  const onRemove = async () => {
    if (item?.id) {
      await dispatch.org.removeOrg(item.id);
      onClose();
    }
  };

  const actionBar = (
    <div>
      <Affix offsetTop={ADMIN_DETAIL_AFFIX_TOP}>
        <PageHeader
          className="platform-item-form-header"
          onBack={() => {
            dispatch.org.setState({ curOrg: undefined });
          }}
          backIcon={<ArrowLeftIcon />}
          title={
            <Headline>
              {item?.id
                ? t('platform.org.form.title.edit')
                : t('platform.org.form.title.create')}
            </Headline>
          }
          extra={
            //org admin can only modify customer org
            isOrgAdmin(user) && !customerOrg
              ? [
                  showStripeButton && item?.id && (
                    <RevxButton
                      key="stripe"
                      brandGreen
                      loading={loadingStripe}
                      onClick={handleStripeAccount}
                    >
                      {stripeAccount?.open
                        ? 'Onboarding Stripe'
                        : 'Link Stripe'}
                    </RevxButton>
                  ),
                  showStripeButton && item?.id && (
                    <Divider type="vertical" key="divider-stripe" />
                  ),
                  <RevxButton onClick={onClose} brandGreen>
                    {t('common.cancel')}
                  </RevxButton>,
                ]
              : [
                  // Sys Admin 专用：修改组织类型按钮
                  isSysAdmin(user) && item?.id && (
                    <RevxButton
                      key="change-org-type"
                      brandGreen
                      loading={changingOrgType}
                      onClick={handleChangeOrgType}
                    >
                      {t('platform.org.changeOrgType')}
                    </RevxButton>
                  ),
                  isSysAdmin(user) && item?.id && (
                    <Divider type="vertical" key="divider-org-type" />
                  ),
                  item?.id && ALBERTSONS_ORGS.includes(item.id) && (
                    <>
                      <CostDistributionImportModal />
                      <Divider type="vertical" key="divider-import" />
                    </>
                  ),
                  showStripeButton && item?.id && (
                    <RevxButton
                      key="stripe"
                      brandGreen
                      loading={loadingStripe}
                      onClick={handleStripeAccount}
                    >
                      {stripeAccount?.open
                        ? 'Onboarding Stripe'
                        : 'Link Stripe'}
                    </RevxButton>
                  ),
                  showStripeButton && item?.id && (
                    <Divider type="vertical" key="divider-stripe" />
                  ),
                  <RevxButton type="primary" brandGreen onClick={onSave}>
                    {t('common.save')}
                  </RevxButton>,
                  ...(item?.id
                    ? [
                        <RevxButton
                          onClick={onSaveAsNew}
                          type="primary"
                          brandGreen
                        >
                          {t('common.saveAsNew')}
                        </RevxButton>,
                      ]
                    : []),
                  <>
                    <Divider type="vertical" />
                    <RevxButton onClick={onClose} brandGreen>
                      {t('common.cancel')}
                    </RevxButton>
                  </>,
                  ...(item?.id
                    ? [
                        <>
                          <Divider type="vertical" />
                          <Popconfirm
                            title={t('common.confirmQuestion')}
                            icon={
                              <QuestionCircleOutlined
                                style={{ color: 'red' }}
                              />
                            }
                            onConfirm={onRemove}
                            okText={t('common.ok')}
                            cancelText={t('common.cancel')}
                          >
                            <RevxButton danger>{t('common.remove')}</RevxButton>
                          </Popconfirm>
                        </>,
                      ]
                    : []),
                ]
          }
        />
      </Affix>
    </div>
  );

  const getLabelKey = (isSourcer: boolean) =>
    isSourcer ? 'sourcerTableConfig' : 'supplierTableConfig';
  const labelCol: ColumnsType<OrgItemLabelTableCol> = [
    {
      title: '',
      dataIndex: 'name',
    },
    {
      title: 'Standard Jobs',
      dataIndex: 'standardLabel',
      render: (text, { isSourcer, type }) => {
        return (
          <>
            <Form.Item name={[getLabelKey(isSourcer), type, 'standardJobs']}>
              <Input defaultValue={text} />
            </Form.Item>
          </>
        );
      },
    },
    {
      title: 'Pre-configured Jobs',
      dataIndex: 'preConfigLabel',
      render: (text, { isSourcer, type }) => {
        return (
          <>
            <Form.Item
              name={[getLabelKey(isSourcer), type, 'preConfiguredSku']}
            >
              <Input defaultValue={text} />
            </Form.Item>
          </>
        );
      },
    },
  ];

  const generateLabelTable = (
    type: WorkspaceTableConfigKey,
    isBilling?: boolean
  ) => {
    let defaultStandardLabel = 'Standard Jobs';
    let defaultPreConfigLabel = 'Pre-configured Jobs';
    if (type === 'billingLabel') {
      defaultStandardLabel = 'Job ready for Payment';
      defaultPreConfigLabel = 'Pre-configured Payment';
    }
    if (type === 'invoicingLabel') {
      defaultStandardLabel = 'Open Invoices';
      defaultPreConfigLabel = 'Open Pre-configured Invoices';
    }
    if (item) {
      const data = (): OrgItemLabelTableCol[] | [] => {
        const { sourcerTableConfig, supplierTableConfig } = item;
        const d: OrgItemLabelTableCol = {
          name: 'Supplier Label',
          standardLabel:
            supplierTableConfig?.[type].standardJobs || defaultStandardLabel,
          preConfigLabel:
            supplierTableConfig?.[type].preConfiguredSku ||
            defaultPreConfigLabel,
          isSourcer: false,
          type,
          role: 'supplier',
        };
        if (isBilling) {
          return [d];
        }
        return [
          d,
          {
            name: 'Sourcer Label',
            standardLabel:
              sourcerTableConfig?.[type].standardJobs || defaultStandardLabel,
            preConfigLabel:
              sourcerTableConfig?.[type].preConfiguredSku ||
              defaultPreConfigLabel,
            isSourcer: true,
            type,
            role: 'sourcer',
          },
        ];
      };
      return (
        <RevxTable
          columns={labelCol}
          dataSource={data()}
          pagination={false}
          rowClassName={(r) => {
            if (
              (r.role === 'supplier' && !groupJobsForSupplierStatus) ||
              (r.role === 'sourcer' && !groupJobsForSourcerStatus)
            ) {
              return 'hide';
            }
            return '';
          }}
        />
      );
    }
  };
  const labelList = useMemo(() => {
    return {
      [inProductionLabel]: generateLabelTable(inProductionLabel),
      [shippingLabel]: generateLabelTable(shippingLabel),
      [billingLabel]: generateLabelTable(billingLabel, true),
      [invoicingLabel]: generateLabelTable(invoicingLabel),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, groupJobsForSupplierStatus, groupJobsForSourcerStatus]);

  const balanceAutoConfirmedPath = ['balanceConfig', 'autoConfirmedEnabled'];
  const balanceAutoPaymentPath = [
    'balanceConfig',
    'autoConfirmedPaymentMethod',
  ];

  // 使用 useEffect 同步表单值到本地状态
  useEffect(() => {
    console.log('item', item);
    if (item) {
      const currentMethods =
        form.getFieldValue(['balanceConfig', 'paymentMethods']) || [];
      const currentDefault = form.getFieldValue([
        'balanceConfig',
        'defaultPaymentMethod',
      ]);
      console.log('form values:', form.getFieldsValue(['balanceConfig']));
      console.log(
        'Syncing payment methods from form:',
        currentMethods,
        currentDefault
      );
      setSelectedPaymentMethods(currentMethods);
      setDefaultPaymentMethod(currentDefault);
    } else {
      setSelectedPaymentMethods([]);
      setDefaultPaymentMethod(undefined);
    }
  }, [item, form]);

  const generateOptions = (selectedValues: string[], defaultValue?: string) => {
    return paymentMethodOptions.map((option) => ({
      ...option,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>{option.label}</span>
          {defaultValue === option.value && (
            <span style={{ fontSize: '12px', color: '#52c41a' }}>✓</span>
          )}
          {selectedValues.includes(option.value) && (
            <button
              type="button"
              style={{
                background: 'none',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                padding: '2px 6px',
                cursor: 'pointer',
                fontSize: '10px',
                color: defaultValue === option.value ? '#52B3A7' : '#1890ff',
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const newDefault =
                  defaultValue === option.value ? undefined : option.value;
                console.log('Setting default payment method to:', newDefault);
                setDefaultPaymentMethod(newDefault);
              }}
            >
              {defaultValue === option.value ? 'Cancel Default' : 'Set Default'}
            </button>
          )}
        </div>
      ),
    }));
  };

  const remitAddressStyle = { marginLeft: 40 };
  return (
    <RevxSpin spinning={saving}>
      {actionBar}
      <div className="platform-item-form-wrapper">
        <Form
          {...formItemLayout}
          layout={'horizontal'}
          form={form}
          className="platform-item-form"
          initialValues={{
            ...item,
            // Suggested from the country when the org has no locale yet, which is what someone
            // would have picked by hand. Only a suggestion: the two are separate fields because
            // the guess is sometimes wrong, so a locale already set is never overwritten.
            locale: item?.locale ?? localeForCountry(item?.country) ?? null,
            // Deliberately not defaulted. Null is "not set", which is what lets the API fall
            // through to the org's locale - default this to IMPERIAL and the first save of
            // every org writes a choice nobody made, and the locale never decides anything.
            measurementUnit: item?.measurementUnit ?? null,
            orderDetailPageConfig: {
              showRequestor: item?.orderDetailPageConfig?.showRequestor ?? true,
              showBillingContact: item?.orderDetailPageConfig?.showBillingContact ?? true,
              showPurchaseOrder: item?.orderDetailPageConfig?.showPurchaseOrder ?? true,
              showCostCenter: item?.orderDetailPageConfig?.showCostCenter ?? true,
              showAccountingLine: item?.orderDetailPageConfig?.showAccountingLine ?? true,
              showOracleBillingCode: item?.orderDetailPageConfig?.showOracleBillingCode ?? false,
            },
            tags: item?.tags ? item.tags.split('|') : [],
            remitSamePhysicalAddress: item?.remitSamePhysicalAddress
              ? [true]
              : [],
          }}
          onValuesChange={async (changedValues, allValues) => {
            if (changedValues) {
              const changeValuesKeys = Object.keys(changedValues);
              if (changeValuesKeys.includes('groupJobsForSupplier')) {
                setGroupJobsForSupplierStatus(
                  changedValues.groupJobsForSupplier
                );
              }
              if (changeValuesKeys.includes('groupJobsForSourcer')) {
                setGroupJobsForSourcerStatus(changedValues.groupJobsForSourcer);
              }

              if (changeValuesKeys.includes('remitSamePhysicalAddress')) {
                syncRemitFields(allValues, form);
              }

              if (
                changeValuesKeys.includes('fax') ||
                changeValuesKeys.includes('addressLine1') ||
                changeValuesKeys.includes('addressLine2') ||
                changeValuesKeys.includes('city') ||
                changeValuesKeys.includes('state') ||
                changeValuesKeys.includes('country') ||
                changeValuesKeys.includes('postalCode') ||
                changeValuesKeys.includes('zoneId') ||
                changeValuesKeys.includes('phone1') ||
                changeValuesKeys.includes('phone2')
              ) {
                syncRemitFields(allValues, form);
              }
            }
          }}
        >
          <AddressValidation
            address={{
              addressLine: form.getFieldValue('addressLine1'),
              city: form.getFieldValue('city'),
              stateProvinceCode: form.getFieldValue('state'),
              countryCode: form.getFieldValue('country'),
              postalCode: form.getFieldValue('postalCode'),
            }}
            onApply={(record) => {
              form.setFieldsValue({
                addressLine1: record.addressLine,
                city: record.city,
                state: record.stateProvinceCode,
                country: record.countryCode,
                postalCode: record.postalCode,
              });
            }}
            ref={addrValidationRef}
          />

          <Form.Item
            label={t('platform.org.form.itemLabels.company')}
            name={['company', 'id']}
            rules={[{ required: true }]}
          >
            <Select
              disabled={Boolean(item?.company)}
              getPopupContainer={(e) => {
                return e as HTMLElement;
              }}
            >
              {companies.map((c) => (
                <Select.Option key={c.id} value={c.id}>
                  {c.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.parent')}
            name="parentId"
          >
            <Input disabled addonAfter={findOrgName(item?.parentId)} />
          </Form.Item>

          <Form.Item
            label={t('common.name')}
            name="name"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item label={t('platform.org.form.itemLabels.logo')} name="logo">
            <RevxInputImage />
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.category')}
            name="category"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input />
          </Form.Item>

          <Form.Item label={t('platform.org.table.columns.tags')} name="tags">
            <Select
              getPopupContainer={(e) => {
                return e as HTMLElement;
              }}
              mode="tags"
            >
              {pipedStrToOptions(item?.tags)}
            </Select>
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.website')}
            name="website"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.table.columns.contactName')}
            name="contactName"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.email')}
            name="email"
            rules={[RULE_EMAIL_TRIM('email'), { required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.accountingEmail')}
            name="accountingEmail"
            rules={[RULE_EMAIL_TRIM('accountingEmail'), { required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.gallerySupportEmail')}
            name="gallerySupportEmail"
            rules={[RULE_EMAIL_TRIM('gallerySupportEmail')]}
          >
            <Input />
          </Form.Item>

          {isSysAdmin(user) && (
            <Form.Item
              label={t('platform.org.form.itemLabels.orgIsSupplier')}
              name="isSupplier"
            >
              <Radio.Group>
                <Radio value={true}>{t('common.yes')}</Radio>
                <Radio value={false}>{t('common.no')}</Radio>
              </Radio.Group>
            </Form.Item>
          )}

          {isOrgAdmin(user) && (saasOrg || customerOrg) && (
            <Form.Item
              label={t('platform.org.form.itemLabels.orgIsSupplier')}
              name="isSupplier"
            >
              <Radio.Group>
                <Radio value={true}>{t('common.yes')}</Radio>
                <Radio value={false}>{t('common.no')}</Radio>
              </Radio.Group>
            </Form.Item>
          )}

          <Form.Item
            label={t('platform.org.form.itemLabels.sourcerManageFiles')}
            name="allowSourcerToManageAllJobFiles"
          >
            <Radio.Group>
              <Radio value={true}>{t('common.yes')}</Radio>
              <Radio value={false}>{t('common.no')}</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.supplierManageFiles')}
            name="allowSupplierToManageAllJobFiles"
          >
            <Radio.Group>
              <Radio value={true}>{t('common.yes')}</Radio>
              <Radio value={false}>{t('common.no')}</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.buyerDigitalProof')}
          >
            <Radio.Group
              value={buyerDigitalProof}
              onChange={(e) => setBuyerDigitalProof(e.target.value)}
            >
              <Radio value="required">
                {t('platform.org.form.itemLabels.required')}
              </Radio>
              <Radio value="optional">
                {t('platform.org.form.itemLabels.optional')}
              </Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.sourcerDigitalProof')}
          >
            <Radio.Group
              value={sourcerDigitalProof}
              onChange={(e) => setSourcerDigitalProof(e.target.value)}
            >
              <Radio value="required">
                {t('platform.org.form.itemLabels.required')}
              </Radio>
              <Radio value="optional">
                {t('platform.org.form.itemLabels.optional')}
              </Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.supplierDigitalProof')}
          >
            <Radio.Group
              value={supplierDigitalProof}
              onChange={(e) => setSupplierDigitalProof(e.target.value)}
            >
              <Radio value="required">
                {t('platform.org.form.itemLabels.required')}
              </Radio>
              <Radio value="optional">
                {t('platform.org.form.itemLabels.optional')}
              </Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.jobsAcceptDigitalProof')}
          >
            <Radio.Group
              value={jobsAcceptDigitalProof}
              onChange={(e) => setJobsAcceptDigitalProof(e.target.value)}
            >
              <Radio value="required">
                {t('platform.org.form.itemLabels.required')}
              </Radio>
              <Radio value="optional">
                {t('platform.org.form.itemLabels.optional')}
              </Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.receiveRFMs')}
            name="receiveRfm"
          >
            <Radio.Group>
              <Radio value={true}>{t('common.yes')}</Radio>
              <Radio value={false}>{t('common.no')}</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.sendJobsToRFM')}
            name="sendJobsToRfm"
          >
            <Radio.Group>
              <Radio value={true}>{t('common.yes')}</Radio>
              <Radio value={false}>{t('common.no')}</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.directlyToOrg')}
            name={balanceAutoConfirmedPath}
          >
            <Radio.Group>
              <Radio value={true}>{t('common.yes')}</Radio>
              <Radio value={false}>{t('common.no')}</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item noStyle={true} dependencies={[balanceAutoConfirmedPath]}>
            {({ getFieldValue }) => {
              const autoConfirmedEnabled = getFieldValue(
                balanceAutoConfirmedPath
              );
              return (
                <div className="payment-methods-content">
                  {autoConfirmedEnabled ? (
                    <Form.Item
                      label={t(
                        'platform.org.form.itemLabels.paymentMethodsToSupport'
                      )}
                      name={balanceAutoPaymentPath}
                      rules={[{ required: true }]}
                    >
                      <Radio.Group
                        options={autoPaymentMethodOptions}
                        onChange={() => {
                          const currentValue =
                            form.getFieldValue([
                              'balanceConfig',
                              'paymentMethods',
                            ]) || [];

                          if (!currentValue.includes(invoice)) {
                            form.setFieldsValue({
                              balanceConfig: {
                                autoConfirmedPaymentMethodId: '',
                                invoiceTerms: undefined,
                              },
                            });
                          } else {
                            form.setFieldsValue({
                              balanceConfig: {
                                autoConfirmedPaymentMethodId: '',
                              },
                            });
                          }
                        }}
                      />
                    </Form.Item>
                  ) : (
                    <Form.Item
                      label={t(
                        'platform.org.form.itemLabels.paymentMethodsToSupport'
                      )}
                      name={['balanceConfig', 'paymentMethods']}
                      rules={[{ required: true }]}
                    >
                      <Checkbox.Group
                        options={generateOptions(
                          selectedPaymentMethods,
                          defaultPaymentMethod
                        )}
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '16px',
                        }}
                        onChange={(checkedValues) => {
                          const currentValue = checkedValues as string[];
                          console.log('currentValue: ', currentValue);

                          // 更新本地状态
                          setSelectedPaymentMethods(currentValue);

                          // 处理 invoice 逻辑
                          if (!currentValue.includes(invoice)) {
                            form.setFieldsValue({
                              balanceConfig: {
                                ...form.getFieldValue('balanceConfig'),
                                invoiceTerms: undefined,
                              },
                            });
                          }

                          // 检查默认支付方式是否还在选中的选项中
                          if (
                            defaultPaymentMethod &&
                            !currentValue.includes(defaultPaymentMethod)
                          ) {
                            setDefaultPaymentMethod(undefined);
                          }
                        }}
                      />
                    </Form.Item>
                  )}
                </div>
              );
            }}
          </Form.Item>
          <Form.Item
            noStyle={true}
            dependencies={[
              balanceAutoConfirmedPath,
              balanceAutoPaymentPath,
              ['balanceConfig', 'paymentMethods'],
            ]}
          >
            {({ getFieldValue }) => {
              const autoConfirmedEnabled = getFieldValue(
                balanceAutoConfirmedPath
              );
              const autoPaymentMethod = getFieldValue(balanceAutoPaymentPath);
              const paymentMethods =
                getFieldValue(['balanceConfig', 'paymentMethods']) || [];

              const showInvoiceTerms = autoConfirmedEnabled
                ? autoPaymentMethod === invoice
                : paymentMethods.includes(invoice);

              return showInvoiceTerms ? (
                <Form.Item
                  label={t('platform.org.form.itemLabels.invoiceTerms')}
                  name={['balanceConfig', 'invoiceTerms']}
                  rules={[
                    { required: true, message: 'Please select invoice terms' },
                  ]}
                >
                  <Select
                    placeholder="Select invoice terms"
                    getPopupContainer={(e) => e as HTMLElement}
                  >
                    {invoiceTermsOptions.map((option) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              ) : null;
            }}
          </Form.Item>
          {/* <Form.Item
            noStyle={true}
            dependencies={[balanceAutoConfirmedPath, balanceAutoPaymentPath]}
          >
            {({ getFieldValue }) => {
              const autoConfirmedEnabled = getFieldValue(
                balanceAutoConfirmedPath
              );
              const isInvoice =
                getFieldValue(balanceAutoPaymentPath) === invoice;
              return (
                <div className="payment-data-content">
                  {autoConfirmedEnabled && (
                    <div className="balance-method-list">
                      <Form.Item
                        label="Payment"
                        name={['balanceConfig', 'autoConfirmedPaymentMethodId']}
                        rules={[
                          {
                            required: autoConfirmedEnabled && !isInvoice,
                            message: 'Please select payment info',
                          },
                        ]}
                      >
                        <Radio.Group>{genRadioOptions(isInvoice)}</Radio.Group>
                      </Form.Item>
                    </div>
                  )}
                </div>
              );
            }}
          </Form.Item> */}

          <Form.Item
            className="platform-item-form-line"
            label="Billing Detail settings:"
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', gap: '16px' }}>
                <Form.Item name={['orderDetailPageConfig', 'showRequestor']} valuePropName="checked" noStyle>
                  <Checkbox style={{ width: 150, whiteSpace: 'nowrap' }}>Requestor</Checkbox>
                </Form.Item>
                <Form.Item name={['orderDetailPageConfig', 'showBillingContact']} valuePropName="checked" noStyle>
                  <Checkbox style={{ width: 150, whiteSpace: 'nowrap' }}>Billing Contact</Checkbox>
                </Form.Item>
                <Form.Item name={['orderDetailPageConfig', 'showPurchaseOrder']} valuePropName="checked" noStyle>
                  <Checkbox style={{ width: 150, whiteSpace: 'nowrap' }}>Purchase Order</Checkbox>
                </Form.Item>
              </div>
              <div style={{ display: 'flex', gap: '16px' }}>
                <Form.Item name={['orderDetailPageConfig', 'showCostCenter']} valuePropName="checked" noStyle>
                  <Checkbox style={{ width: 150, whiteSpace: 'nowrap' }}>Cost Center</Checkbox>
                </Form.Item>
                <Form.Item name={['orderDetailPageConfig', 'showAccountingLine']} valuePropName="checked" noStyle>
                  <Checkbox style={{ width: 150, whiteSpace: 'nowrap' }}>Accounting Line</Checkbox>
                </Form.Item>
                <Form.Item name={['orderDetailPageConfig', 'showOracleBillingCode']} valuePropName="checked" noStyle>
                  <Checkbox style={{ width: 180, whiteSpace: 'nowrap' }}>Oracle Billing Code</Checkbox>
                </Form.Item>
              </div>
            </div>
          </Form.Item>


          <Form.Item
            className="platform-item-form-line"
            label={t('platform.org.form.itemLabels.supplierViewJobSummary')}
            name="supplierViewJobSummary"
          >
            <Radio.Group>
              <Radio value={true}>{t('common.yes')}</Radio>
              <Radio value={false}>{t('common.no')}</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            className="platform-item-form-line"
            label={t('platform.org.form.itemLabels.sourcerViewBillingContact')}
            name="sourcerViewBillingContact"
          >
            <Radio.Group>
              <Radio value={true}>{t('common.yes')}</Radio>
              <Radio value={false}>{t('common.no')}</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            className="platform-item-form-line"
            label={t('platform.org.form.itemLabels.supplierViewBillingContact')}
            name="supplierViewBillingContact"
          >
            <Radio.Group>
              <Radio value={true}>{t('common.yes')}</Radio>
              <Radio value={false}>{t('common.no')}</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            className="platform-item-form-line"
            label={t(
              'platform.org.form.itemLabels.sourcerAdjustBillingContact'
            )}
            name="sourcerAdjustBillingContact"
          >
            <Radio.Group>
              <Radio value={true}>{t('common.yes')}</Radio>
              <Radio value={false}>{t('common.no')}</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            className="platform-item-form-line"
            label={t(
              'platform.org.form.itemLabels.supplierAdjustBillingContact'
            )}
            name="supplierAdjustBillingContact"
          >
            <Radio.Group>
              <Radio value={true}>{t('common.yes')}</Radio>
              <Radio value={false}>{t('common.no')}</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            className="platform-item-form-line"
            label={t('platform.org.form.itemLabels.defaultSearchWhitePaper')}
            name="defaultSearchWhitePaper"
          >
            <Radio.Group>
              <Radio value={true}>{t('common.yes')}</Radio>
              <Radio value={false}>{t('common.no')}</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.supplierTabs')}
            name="groupJobsForSupplier"
          >
            <Radio.Group>
              <Radio value={true}>{t('common.yes')}</Radio>
              <Radio value={false}>{t('common.no')}</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label={t('platform.org.form.itemLabels.sourcerTabs')}
            name="groupJobsForSourcer"
          >
            <Radio.Group>
              <Radio value={true}>{t('common.yes')}</Radio>
              <Radio value={false}>{t('common.no')}</Radio>
            </Radio.Group>
          </Form.Item>

          {(groupJobsForSupplierStatus || groupJobsForSourcerStatus) && (
            <>
              <Form.Item label="In Production">
                {labelList?.inProductionLabel}
              </Form.Item>
              <Form.Item label="Shipping">{labelList.shippingLabel}</Form.Item>
              <Form.Item label="Billing">{labelList.billingLabel}</Form.Item>
              <Form.Item label="Invoicing">
                {labelList.invoicingLabel}
              </Form.Item>
            </>
          )}

          <Form.Item
            label={t('platform.org.form.itemLabels.bankName')}
            name="bankName"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.bankAccount')}
            name="bankAccountNumber"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.routingNumber')}
            name="routingNumber"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.swiftCode')}
            name="swiftCode"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('workspace.detail.po.resaleNumber')}
            name="resaleNumber"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input />
          </Form.Item>
          {/* 
          <Form.Item
            label={t('platform.org.form.itemLabels.balanceBuyerId')}
            name={['balanceConfig', 'buyerId']}
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input onChange={runAsync} />
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.balanceVendorId')}
            name={['balanceConfig', 'invoiceVendorId']}
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input />
          </Form.Item> */}

          <Form.Item
            label={t('platform.org.form.itemLabels.addressLine1')}
            name="addressLine1"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.addressLine2')}
            name="addressLine2"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.table.columns.city')}
            name="city"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) => prev.country !== cur.country}
          >
            {({ getFieldValue }) => (
              <Form.Item
                label={t('platform.org.table.columns.state')}
                name="state"
                // only US addresses need a state; most countries have none (PRD-746).
                // `dependencies` re-validates when country changes, and the rule reads the
                // country as it validates, so switching to a country without states clears
                // the error instead of leaving it stuck.
                dependencies={['country']}
                required={isUnitedStates(getFieldValue('country'))}
                rules={[
                  RULE_TEXT_LEN_255,
                  ({ getFieldValue: getCurrent }) => ({
                    required: isUnitedStates(getCurrent('country')),
                  }),
                ]}
              >
                {/* the list is US state codes, so offer it only for US addresses;
                    elsewhere a province is free text (PRD-746) */}
                {isUnitedStates(getFieldValue('country')) ? (
                  <AutoComplete filterOption>
                    {statesAbbrevs.map((s) => (
                      <AutoComplete.Option value={s} key={s}>
                        {s}
                      </AutoComplete.Option>
                    ))}
                  </AutoComplete>
                ) : (
                  <Input />
                )}
              </Form.Item>
            )}
          </Form.Item>

          <Form.Item
            label={t('platform.org.table.columns.country')}
            name="country"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.postalCode')}
            name="postalCode"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.table.columns.timezone')}
            name={'zoneId'}
            rules={[{ required: true }]}
          >
            <AutoComplete filterOption>
              {timezones.map((tz) => (
                <AutoComplete.Option key={tz.tzCode} value={tz.tzCode}>
                  {tz.tzCode}
                </AutoComplete.Option>
              ))}
            </AutoComplete>
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.locale')}
            name="locale"
            extra={t('platform.org.form.itemLabels.localeHint')}
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="children"
              placeholder={t('platform.org.form.itemLabels.localeUnset')}
            >
              {ORG_LOCALES.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {`${option.label} — ${option.value}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.measurementUnit')}
            name="measurementUnit"
            extra={t('platform.org.form.itemLabels.measurementUnitHint')}
          >
            <Select allowClear placeholder={t('platform.org.form.itemLabels.measurementUnitUnset')}>
              <Select.Option value={null as unknown as string}>
                {t('platform.org.form.itemLabels.measurementUnitUnset')}
              </Select.Option>
              <Select.Option value={MeasurementUnitEnum.IMPERIAL}>
                {t('common.measurementImperial')}
              </Select.Option>
              <Select.Option value={MeasurementUnitEnum.METRIC}>
                {t('common.measurementMetric')}
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.phone1')}
            name="phone1"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.phone2')}
            name="phone2"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.fax')}
            name="fax"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.remitAddress')}
            name={['remitSamePhysicalAddress']}
          >
            <Checkbox.Group options={remitAddressOptions} />
          </Form.Item>
          <Form.Item noStyle={true} dependencies={['remitSamePhysicalAddress']}>
            {({ getFieldValue }) => {
              const remitValue = getFieldValue('remitSamePhysicalAddress');
              const isRemitSameAsPhysical =
                Array.isArray(remitValue) && remitValue.includes(true);
              return (
                <Form.Item
                  label={t('platform.org.form.itemLabels.remitAddressLine1')}
                  name="remitAddressLine1"
                  rules={[RULE_TEXT_LEN_255]}
                  style={remitAddressStyle}
                >
                  <Input disabled={isRemitSameAsPhysical} />
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item noStyle={true} dependencies={['remitSamePhysicalAddress']}>
            {({ getFieldValue }) => {
              const remitValue = getFieldValue('remitSamePhysicalAddress');
              const isRemitSameAsPhysical =
                Array.isArray(remitValue) && remitValue.includes(true);
              return (
                <Form.Item
                  label={t('platform.org.form.itemLabels.remitAddressLine2')}
                  name="remitAddressLine2"
                  rules={[RULE_TEXT_LEN_255]}
                  style={remitAddressStyle}
                >
                  <Input disabled={isRemitSameAsPhysical} />
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item noStyle={true} dependencies={['remitSamePhysicalAddress']}>
            {({ getFieldValue }) => {
              const remitValue = getFieldValue('remitSamePhysicalAddress');
              const isRemitSameAsPhysical =
                Array.isArray(remitValue) && remitValue.includes(true);

              return (
                <Form.Item
                  label={t('platform.org.form.itemLabels.remitCity')}
                  name="remitCity"
                  rules={[RULE_TEXT_LEN_255, { required: true }]}
                  style={remitAddressStyle}
                >
                  <Input disabled={isRemitSameAsPhysical} />
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item
            noStyle={true}
            dependencies={['remitSamePhysicalAddress', 'remitCountry']}
          >
            {({ getFieldValue }) => {
              const remitValue = getFieldValue('remitSamePhysicalAddress');
              const isRemitSameAsPhysical =
                Array.isArray(remitValue) && remitValue.includes(true);

              return (
                <Form.Item
                  label={t('platform.org.form.itemLabels.remitState')}
                  name="remitState"
                  // only US addresses need a state; most countries have none (PRD-746).
                  // `dependencies` re-validates when the country changes, and the rule reads
                  // it as it validates, so the error clears instead of sticking.
                  dependencies={['remitCountry']}
                  required={isUnitedStates(getFieldValue('remitCountry'))}
                  rules={[
                    RULE_TEXT_LEN_255,
                    ({ getFieldValue: getCurrent }) => ({
                      required: isUnitedStates(getCurrent('remitCountry')),
                    }),
                  ]}
                  style={remitAddressStyle}
                >
                  {/* US state codes only apply to US addresses (PRD-746) */}
                  {isUnitedStates(getFieldValue('remitCountry')) ? (
                    <AutoComplete filterOption disabled={isRemitSameAsPhysical}>
                      {statesAbbrevs.map((s) => (
                        <AutoComplete.Option value={s} key={s}>
                          {s}
                        </AutoComplete.Option>
                      ))}
                    </AutoComplete>
                  ) : (
                    <Input disabled={isRemitSameAsPhysical} />
                  )}
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item noStyle={true} dependencies={['remitSamePhysicalAddress']}>
            {({ getFieldValue }) => {
              //const isRemitSameAsPhysical = getFieldValue('remitSamePhysicalAddress') == true;
              const remitValue = getFieldValue('remitSamePhysicalAddress');
              const isRemitSameAsPhysical =
                Array.isArray(remitValue) && remitValue.includes(true);

              return (
                <Form.Item
                  label={t('platform.org.form.itemLabels.remitCountry')}
                  name="remitCountry"
                  rules={[RULE_TEXT_LEN_255, { required: true }]}
                  style={remitAddressStyle}
                >
                  <Input disabled={isRemitSameAsPhysical} />
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item noStyle={true} dependencies={['remitSamePhysicalAddress']}>
            {({ getFieldValue }) => {
              const remitValue = getFieldValue('remitSamePhysicalAddress');
              const isRemitSameAsPhysical =
                Array.isArray(remitValue) && remitValue.includes(true);

              return (
                <Form.Item
                  label={t('platform.org.form.itemLabels.remitPostalCode')}
                  name="remitPostalCode"
                  rules={[RULE_TEXT_LEN_255, { required: true }]}
                  style={remitAddressStyle}
                >
                  <Input disabled={isRemitSameAsPhysical} />
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item noStyle={true} dependencies={['remitSamePhysicalAddress']}>
            {({ getFieldValue }) => {
              const remitValue = getFieldValue('remitSamePhysicalAddress');
              const isRemitSameAsPhysical =
                Array.isArray(remitValue) && remitValue.includes(true);

              return (
                <Form.Item
                  label={t('platform.org.form.itemLabels.remitZoneId')}
                  name={'remitZoneId'}
                  rules={[{ required: true }]}
                  style={remitAddressStyle}
                >
                  <AutoComplete filterOption disabled={isRemitSameAsPhysical}>
                    {timezones.map((tz) => (
                      <AutoComplete.Option key={tz.tzCode} value={tz.tzCode}>
                        {tz.tzCode}
                      </AutoComplete.Option>
                    ))}
                  </AutoComplete>
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item noStyle={true} dependencies={['remitSamePhysicalAddress']}>
            {({ getFieldValue }) => {
              //const isRemitSameAsPhysical = getFieldValue('remitSamePhysicalAddress') == true;
              const remitValue = getFieldValue('remitSamePhysicalAddress');
              const isRemitSameAsPhysical =
                Array.isArray(remitValue) && remitValue.includes(true);

              return (
                <Form.Item
                  label={t('platform.org.form.itemLabels.remitPhone1')}
                  name="remitPhone1"
                  rules={[RULE_TEXT_LEN_255]}
                  style={remitAddressStyle}
                >
                  <Input disabled={isRemitSameAsPhysical} />
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item noStyle={true} dependencies={['remitSamePhysicalAddress']}>
            {({ getFieldValue }) => {
              const remitValue = getFieldValue('remitSamePhysicalAddress');
              const isRemitSameAsPhysical =
                Array.isArray(remitValue) && remitValue.includes(true);

              return (
                <Form.Item
                  label={t('platform.org.form.itemLabels.remitPhone2')}
                  name="remitPhone2"
                  rules={[RULE_TEXT_LEN_255]}
                  style={remitAddressStyle}
                >
                  <Input disabled={isRemitSameAsPhysical} />
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item noStyle={true} dependencies={['remitSamePhysicalAddress']}>
            {({ getFieldValue }) => {
              const remitValue = getFieldValue('remitSamePhysicalAddress');
              const isRemitSameAsPhysical =
                Array.isArray(remitValue) && remitValue.includes(true);

              return (
                <Form.Item
                  label={t('platform.org.form.itemLabels.remitFax')}
                  name="remitFax"
                  rules={[RULE_TEXT_LEN_255]}
                  style={remitAddressStyle}
                >
                  <Input disabled={isRemitSameAsPhysical} />
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.remitEmailAddress')}
            name="remitEmailAddress"
            rules={[RULE_EMAIL_TRIM('email'), { required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.accountingLine')}
            name="accountingLine"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.costCenter')}
            name="costCenter"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.ownership')}
            name="ownership"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.totalEmployees')}
            name="totalEmployees"
          >
            <InputNumber min="0" precision={0} />
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.annualRevenues')}
            name="annualRevenues"
            rules={[RULE_NUMBER]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.yearEstablished')}
            name="yearEstablished"
            rules={[RULE_YEAR()]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.overallScore')}
            name="overallScore"
          >
            <InputNumber min="0" precision={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item label={t('platform.org.form.itemLabels.cost')} name="cost">
            <InputNumber min="0" precision={0} />
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.integration')}
            name="integration"
          >
            <InputNumber min="0" precision={0} />
          </Form.Item>
          <Form.Item
            label={t('platform.org.form.itemLabels.capabilities')}
            name="capabilities"
          >
            <InputNumber min="0" precision={0} />
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.continuity')}
            name="continuity"
          >
            <InputNumber min="0" precision={0} />
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.reliability')}
            name="reliability"
          >
            <InputNumber min="0" precision={0} />
          </Form.Item>
          <Form.Item
            label={t('platform.org.form.itemLabels.invoiceSendersList')}
            name="invoiceSendersList"
            rules={[
              {
                validator: (rule, value) => {
                  const emailList = emailStrToArray(value);
                  const emailRegex =
                    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                  for (const email of emailList) {
                    if (!emailRegex.test(email)) {
                      return Promise.reject('Invalid email format');
                    }
                  }
                  if (emailList.length > invoiceSendersListMaxLength) {
                    return Promise.reject(
                      `Maximum ${invoiceSendersListMaxLength} emails allowed`
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="Input sender emails here and separate them with commas." />
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.overrideInvoiceName')}
          >
            <div
              style={{
                display: 'flex',
                gap: '16px',
                alignItems: 'flex-start',
              }}
            >
              <Form.Item
                name="defaultInvoiceContactId"
                style={{ flex: 1, width: '240px', marginBottom: 0 }}
              >
                <Select<number>
                  showSearch
                  placeholder="Select Invoice Contact"
                  allowClear
                  value={defaultInvoiceContact?.id}
                  filterOption={(input, option) => {
                    return (
                      option?.children
                        ?.toLowerCase()
                        ?.indexOf(input?.toLowerCase()) >= 0
                    );
                  }}
                  onChange={(val) => {
                    const newContact = orgContacts.find((c) => c.id === val);
                    setDefaultInvoiceContact(newContact);

                    if (newContact && newContact.addresses?.length > 0) {
                      // 优先选择默认地址，如果没有默认地址则选择第一个
                      const defaultAddress = newContact.addresses.find(
                        (addr) => addr.id === newContact.defaultAddressId
                      );
                      const selectedAddress =
                        defaultAddress || newContact.addresses[0];

                      console.log('select contact id ', val);
                      console.log(
                        'Selected contact:',
                        newContact.firstName,
                        newContact.lastName
                      );
                      console.log(
                        'Default address ID:',
                        newContact.defaultAddressId
                      );
                      console.log('Selected address:', selectedAddress);

                      setDefaultInvoiceAddress(selectedAddress);
                      form.setFieldsValue({
                        defaultInvoiceAddressId: selectedAddress.id,
                      });
                    } else {
                      setDefaultInvoiceAddress(undefined);
                      form.setFieldsValue({
                        defaultInvoiceAddressId: undefined,
                      });
                    }
                  }}
                  loading={loadingOrgContacts}
                  notFoundContent={
                    loadingOrgContacts ? (
                      <Skeleton.Input active size="small" />
                    ) : (
                      <div style={{ paddingLeft: '12px' }}>No data</div>
                    )
                  }
                >
                  {orgContacts.map((contact) => (
                    <Select.Option key={contact.id} value={contact.id}>
                      {[
                        `${contact.firstName} ${contact.lastName}`,
                        contact.company,
                      ]
                        .filter((item) => item)
                        .join(', ')}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="defaultInvoiceAddressId"
                style={{ flex: 3, width: '650px', marginBottom: 0 }}
              >
                <Select<number>
                  placeholder="Select Invoice Address"
                  allowClear
                  disabled={!defaultInvoiceContact}
                  value={defaultInvoiceAddress?.id}
                  onChange={(val) => {
                    const newAddress = defaultInvoiceContact?.addresses?.find(
                      (a) => a.id === val
                    );
                    setDefaultInvoiceAddress(newAddress);
                  }}
                  notFoundContent={
                    !defaultInvoiceContact ? (
                      'Please select contact first'
                    ) : (
                      <div style={{ paddingLeft: '12px' }}>
                        No addresses found
                      </div>
                    )
                  }
                >
                  {defaultInvoiceAddressOptions.map((addr) => (
                    <Select.Option key={addr.id} value={addr.id}>
                      {addressTranslator?.getShippingAddressByContact(addr)}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
          </Form.Item>

          {customerOrg && (
            <Form.Item
              label={t('platform.org.form.itemLabels.salesRep')}
              name="salesRep"
              rules={[RULE_TEXT_LEN_255]}
            >
              <Input />
            </Form.Item>
          )}

          <Form.Item
            className="platform-item-form-line"
            label="Hide Project"
            name="hideProject"
            valuePropName="checked"
          >
            <Checkbox />
          </Form.Item>

          <Form.Item
            label={t('platform.org.addr.form.labels.ignoreShippingValidation')}
            name="ignoreAddressValidation"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            label={t('platform.org.form.itemLabels.orgType')}
            name="orgType"
            hidden={true}
          >
            <Input />
          </Form.Item>
        </Form>
      </div>

      {/* 组织类型修改模态框 */}
      <OrgTypeSelectModal
        visible={orgTypeModalVisible}
        onCancel={handleOrgTypeCancel}
        onConfirm={handleOrgTypeConfirm}
        defaultType={item?.orgType}
      />
    </RevxSpin>
  );
};
export default OrgItemForm;
